import type {SourceChunkLayout} from '@github-ui/taillights-worker/source-chunks'

export const windowedSourceChunkLines = 30
/** Chunks a virtualized source paints before the client knows its viewport, as in the server render. */
export const initialWindowChunks = 2
const guardLines = 8

export interface SourceWindow {
  first: number
  last: number
  /** First block of the window without any lead: the keyboard entry point, independent of read-ahead. */
  entry: number
  /** Last block of the window without any lead; anchor lines only take keyboard entry from `entry` to here. */
  entryLast: number
  viewportHeight: number
}

export function gutterFocusScrollTop(
  rowTop: number,
  lineHeight: number,
  scrollPaddingTop: number,
  scrollTop: number,
  viewportHeight: number,
) {
  // Gutter targets exceed the source row pitch; keep a neighboring row visible.
  return Math.max(
    0,
    Math.min(rowTop - lineHeight - scrollPaddingTop, Math.max(scrollTop, rowTop + 2 * lineHeight - viewportHeight)),
  )
}

export function sourceBlockAt(blocks: readonly SourceChunkLayout[], displayLine: number) {
  let low = 0
  let high = blocks.length
  while (low < high) {
    const middle = (low + high) >>> 1
    const block = blocks[middle]
    if (!block) throw new RangeError('Invalid source block index')
    if (block.displayStart + Math.max(1, block.lineNumbers.length) <= displayLine) low = middle + 1
    else high = middle
  }
  return Math.max(0, Math.min(blocks.length - 1, low))
}

export function sourceWindowForViewport(
  blocks: readonly SourceChunkLayout[],
  lineHeight: number,
  scrollTop: number,
  viewportHeight: number,
  lead = 0,
): SourceWindow {
  const entryLine = Math.max(0, Math.floor(scrollTop / lineHeight) - guardLines)
  const entryLastLine = Math.max(0, Math.ceil((scrollTop + viewportHeight) / lineHeight) + guardLines - 1)
  // A signed lead extends the window ahead of fast scrolling so compositor-only frames keep moving rows.
  const firstLine = Math.max(0, Math.floor((scrollTop + Math.min(0, lead)) / lineHeight) - guardLines)
  const lastLine = Math.max(
    0,
    Math.ceil((scrollTop + viewportHeight + Math.max(0, lead)) / lineHeight) + guardLines - 1,
  )
  return {
    first: sourceBlockAt(blocks, firstLine),
    last: sourceBlockAt(blocks, lastLine),
    entry: sourceBlockAt(blocks, entryLine),
    entryLast: sourceBlockAt(blocks, entryLastLine),
    viewportHeight,
  }
}

const frameMs = 1000 / 60
const leadFrames = 9
const maxLeadViewports = 2
const minStartCoveredFrames = 4
const minCoveredFrames = 2.5
const maxGrowth = 1.25
const minLeadSpeed = 4
const maxSampleGap = 100

export interface ScrollSample {
  time: number
  scrollTop: number
  /** Signed speed in px/ms since the previous sample; 0 after a gap. */
  speed: number
  /** Signed lead in px for the window committed with this sample. */
  lead: number
  /** Set once the scroll moves too fast to read ahead; holds until it settles. */
  fast: boolean
}

/**
 * Next scroll sample with its lead: about nine frames of travel at the measured speed, rounded up to whole chunks and
 * capped at two viewports. Only steady scrolling gets one, as when holding the scrollbar track or Page Down: it starts
 * with four frames of travel covered, and stops for the rest of the scroll once it covers less than two and a half.
 */
export function nextScrollSample(
  previous: ScrollSample | undefined,
  time: number,
  scrollTop: number,
  viewportHeight: number,
  lineHeight: number,
): ScrollSample {
  const sample = {time, scrollTop, speed: 0, lead: 0, fast: false}
  const elapsed = previous ? time - previous.time : 0
  if (!previous || elapsed <= 0 || elapsed > maxSampleGap) return sample
  const distance = scrollTop - previous.scrollTop
  sample.speed = distance / elapsed
  const speed = Math.abs(sample.speed)
  const cap = viewportHeight * maxLeadViewports
  const coveredFrames = cap / (speed * frameMs)
  // Chrome can't rasterize read-ahead in time for very fast scrolling (the smooth Top scroll, flings, thumb jumps), and
  // a lead started while it slows down shows blank rows; the pinned slice shows the previous rows instead.
  sample.fast = previous.fast || Math.abs(distance) > viewportHeight * 2 || coveredFrames < minCoveredFrames
  if (sample.fast || speed < minLeadSpeed) return sample
  // A scroll that changes speed (thumb drags, the start of a smooth scroll) reaches read-ahead rows before they're
  // rasterized; steady ones (the scrollbar track, Page Down) keep within a quarter of their speed between samples.
  const growth = sample.speed / previous.speed
  const leading = Math.sign(previous.lead) === Math.sign(sample.speed)
  const steady = growth >= 1 / maxGrowth && growth <= maxGrowth
  if (!steady || (!leading && coveredFrames < minStartCoveredFrames)) return sample
  const chunkHeight = windowedSourceChunkLines * lineHeight
  const travel = Math.min(speed * frameMs * leadFrames, cap)
  sample.lead = Math.sign(sample.speed) * Math.min(Math.ceil(travel / chunkHeight) * chunkHeight, cap)
  return sample
}

export function sameSourceWindow(a: SourceWindow, b: SourceWindow) {
  return (
    a.first === b.first &&
    a.last === b.last &&
    a.entry === b.entry &&
    a.entryLast === b.entryLast &&
    a.viewportHeight === b.viewportHeight
  )
}

const maxLayeredChunks = 8

/**
 * Chunks that get their own compositor layer: only those mounted ahead of the viewport while scrolling fast, kept for
 * as long as they stay mounted. Moving the slice then shifts their layers instead of re-rastering them, while chunks
 * mounted in view (initial load, jumps) keep the shared layer that Chrome holds until it is rasterized. Folded code
 * packs many small chunks into a lead, so at most eight get layers, nearest to the viewport first.
 */
export function layeredChunks(
  current: ReadonlySet<SourceChunkLayout['anchor']>,
  blocks: readonly SourceChunkLayout[],
  previous: SourceWindow | undefined,
  next: SourceWindow,
  promote: boolean,
  viewportTopLine: number,
  viewportBottomLine: number,
): ReadonlySet<SourceChunkLayout['anchor']> {
  const result = new Set<SourceChunkLayout['anchor']>()
  const offscreen: Array<{anchor: SourceChunkLayout['anchor']; distance: number}> = []
  for (let index = next.first; index <= next.last; index++) {
    const block = blocks[index]
    if (!block) continue
    const end = block.displayStart + Math.max(1, block.lineNumbers.length)
    const retained = previous !== undefined && index >= previous.first && index <= previous.last
    if (retained) {
      if (current.has(block.anchor)) result.add(block.anchor)
    } else if (promote && (block.displayStart >= viewportBottomLine || end <= viewportTopLine)) {
      offscreen.push({
        anchor: block.anchor,
        distance: Math.max(block.displayStart - viewportBottomLine, viewportTopLine - end),
      })
    }
  }
  offscreen.sort((a, b) => a.distance - b.distance)
  for (const {anchor} of offscreen) {
    if (result.size >= maxLayeredChunks) break
    result.add(anchor)
  }
  return result.size === current.size && [...result].every(anchor => current.has(anchor)) ? current : result
}
